import { useCopyToClipboard } from '@lfg/core/hooks';
import { Box, Divider, Menu, Stack, Typography, alpha } from '@mui/material';
import moment from 'moment-timezone';
import { type MouseEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';

import CopyIcon from 'src/assets/icons/copy.svg';
import ExportIcon from 'src/assets/icons/export.svg';
import FilterIcon from 'src/assets/icons/filter.svg';
import BasicCheckBoxGroup from 'src/components/BasicCheckboxGroup';
import { PrimaryButton } from 'src/components/Buttons/PrimaryButton';
import { SecondaryButton } from 'src/components/Buttons/SecondaryButton';
import { StatusChip } from 'src/components/StatusChip';
import BasicTable, { type GridColDef } from 'src/components/table';
import { OTC_STATUS } from 'src/global/constants/otc.contant';
import { Roles } from 'src/global/constants/role.constant';
import { DEAL_STATE, DEAL_STATUS } from 'src/global/enums/otc.enum';
import type { OTCSchemaType } from 'src/global/schemas/otc.schema';
import type { MetaRequest } from 'src/global/types/otc.type';
import { useGA } from 'src/hooks/useGA';
import { useRoles } from 'src/provider/RoleProvider';
import { useGetOTCSalesQuery } from 'src/store/services/otc.service';
import { formatNumberWithComma, generateUrlOtc } from 'src/utils/helpers';

const OtcSales = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { address } = useAccount();
  const [_addr, copyURL] = useCopyToClipboard();
  const [query, setQuery] = useState<MetaRequest>({ page: 0, size: 10 });
  const { roles } = useRoles();
  const isAdmin = roles.includes(Roles.ADMIN);
  const { trackButtonClickEvent } = useGA();
  const {
    data: OTCList,
    refetch: getListOtc,
    isLoading,
  } = useGetOTCSalesQuery(
    { programId: id, creator: address, params: { ...query, page: query.page + 1 } },
    { skip: !id || !address },
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (query) getListOtc();
  }, [JSON.stringify(query)]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCreateOTC = () => {
    trackButtonClickEvent({ name: 'create_otc_sale', address });
    const params = new URLSearchParams();
    params.set('action', 'create_otc');
    navigate(`otc/new?${params.toString()}`);
  };

  const onCopyUrl = (url: string) => {
    copyURL(url);
    toast.success('Copied URL to clipboard.');
  };

  const navigateDetail = (otcId?: string) => {
    navigate(`/core-sale/${id}/otc/${otcId}`);
  };

  const columns: GridColDef<OTCSchemaType>[] = [
    {
      field: 'url',
      headerName: 'OTC URL',
      renderCell: (_title, row) => {
        const url = generateUrlOtc(row?.sale as string, row?.id as string);
        return (
          <Stack gap='8px' alignItems='center' justifyContent='start' flexDirection='row' height={'fit-content'}>
            <Typography
              component={'p'}
              title={url}
              className='flex-1 truncate text-body_m text-white font-gotham max-w-[130px]'>
              {url}
            </Typography>
            <Typography
              sx={{ cursor: 'pointer', '&:hover': { color: 'white' } }}
              component={'a'}
              href={url}
              target='_blank'
              className='w-fit leading-[12px]'
              color='white'>
              <ExportIcon />
            </Typography>
            <Typography sx={{ cursor: 'pointer' }} color='white' onClick={() => onCopyUrl(url)} component={'span'}>
              <CopyIcon />
            </Typography>
          </Stack>
        );
      },
    },

    {
      field: 'createdAt',
      headerName: 'Created Date',
      width: 150,
      renderCell: createdDate => {
        const date = createdDate ? moment(createdDate).format('MM-DD-YYYY') : '-';
        return (
          <Typography color='white' className='font-gotham text-body_m'>
            {date}
          </Typography>
        );
      },
    },
    {
      field: 'a',
      headerName: 'Amount',
      width: 120,
      renderCell: (_, row) => {
        const amount = row.amountNode * row.pricePerNode;
        return (
          <Typography color='white' className='font-gotham text-body_m'>
            ${formatNumberWithComma(amount)}
          </Typography>
        );
      },
    },
    {
      field: 'state',
      headerName: 'Payment Status',
      width: 126,
      renderCell: (status, row) => {
        const type = OTC_STATUS[status];
        const url = `https://sepolia.etherscan.io/address/${row.sale}`;
        return (
          <Stack gap={'4px'} flexDirection={'row'} alignItems={'center'}>
            <StatusChip type={type} value={status} />
            <Typography sx={{ '&:hover': { color: 'white' } }} href={url} target='_blank' color='white' component={'a'}>
              <ExportIcon />
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'status',
      headerName: 'OTC Status',
      width: 126,
      renderCell: status => {
        const type = OTC_STATUS[status];
        return <StatusChip type={type} value={status} />;
      },
    },
    {
      field: '1',
      headerName: 'Action',
      renderCell: (_, row) => {
        return (
          <Typography
            onClick={() => navigateDetail(row.id)}
            className='text-body_m font-gotham text-white underline hover:opacity-50 cursor-pointer'
            component={'p'}>
            View Details
          </Typography>
        );
      },
    },
  ];

  return (
    <Box position={'relative'} width={780} minHeight={500}>
      <SecondaryButton onClick={handleClick} sx={{ position: 'absolute', right: 0, top: -70 }} height={'40px'}>
        <FilterIcon />
        <Typography className='text-head_xs font-gotham'>Filters</Typography>
      </SecondaryButton>
      <BasicTable
        loading={isLoading}
        total={10}
        columns={columns}
        data={OTCList || []}
        page={query.page}
        onChangePage={page =>
          setQuery((state: any) => ({
            ...state,
            page,
          }))
        }
        size={query.size}
        onChangePageSize={size =>
          setQuery((state: any) => ({
            ...state,
            size,
          }))
        }
      />
      <Stack display={isAdmin ? 'flex' : 'none'} width={'100%'} alignItems={'center'} marginTop={2}>
        <PrimaryButton onClick={handleCreateOTC} height={'48px'}>
          <Typography className='text-head_s font-gotham'>Create OTC Sale</Typography>
        </PrimaryButton>
      </Stack>
      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ '& .MuiMenu-list': { paddingTop: 0, paddingBottom: 0, bgcolor: '#060912' } }}>
        <Box padding={'16px'}>
          <Typography color='#fff' fontWeight={500} className='text-head_xs font-gotham'>
            By Payment Status
          </Typography>
          <BasicCheckBoxGroup
            options={[
              { label: 'Paid', value: DEAL_STATE.PAID },
              { label: 'Unpaid', value: DEAL_STATE.UNPAID },
            ]}
            onChange={val => {
              setQuery(state => ({ ...state, filter: { ...state.filter, state: val } }));
            }}
            value={(query?.filter?.state as string[]) || []}
          />
          <Divider sx={{ borderColor: alpha('rgba(255, 255, 255)', 0.15), pt: 1, mb: 1 }} />
          <Typography color='#fff' fontWeight={500} className='text-head_xs font-gotham'>
            By OTC Status
          </Typography>
          <BasicCheckBoxGroup
            options={[
              { label: 'Live', value: DEAL_STATUS.LIVE },
              { label: 'Archived', value: DEAL_STATUS.ARCHIVED },
            ]}
            onChange={val => {
              setQuery(state => ({ ...state, filter: { ...state.filter, status: val } }));
            }}
            value={(query?.filter?.status as string[]) || []}
          />
        </Box>
      </Menu>
    </Box>
  );
};

export default OtcSales;
